import {
  handleSubmitMultilingualForm,
  useCreateHomepage,
  useGeneralSettingsFormData,
  useGetHomepageFormData,
  useUpdateGeneralSettings,
  useUpdateHomepage,
} from "@/api/cms.ts";
import { GeneralSettingsForm } from "@/components/cms/GeneralSettingsForm";
import HomepageForm from "@/components/cms/HomepageForm.tsx";
import { Switch } from "@/components/form-items/AbstractForm";
import { DoubleMultilingualField } from "@/components/form-items/MultilingualField";
import { useAuth } from "@/context/auth/AuthContext";
import { useToast } from "@/hooks/use-toast";
import {
  constructLocaleDict,
  multilingualFieldSchema,
  SupportedLocale,
} from "@/i18n";
import CMSLayout from "@/layout/CMSLayout.tsx";
import {
  cmsPageSchema,
  generalSettingsSchema,
  homepageContentSchema,
} from "@/lib/cms/cms-types";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

const homePageFormSchema = multilingualFieldSchema(
  cmsPageSchema
    .pick({
      published: true,
    })
    .merge(homepageContentSchema.omit({ id: true }))
    .optional(),
);

export type CmsHomepageForm = z.infer<typeof homePageFormSchema>;

export type CmsGeneralSettingsForm = z.infer<typeof generalSettingsSchema>;

const CMS = () => {
  const { t } = useTranslation();
  const { instanceData } = useAuth();

  const { toast } = useToast();

  const create = useCreateHomepage();
  const update = useUpdateHomepage();
  const { data: homePageData } = useGetHomepageFormData();
  const { data: generalSettingsData } = useGeneralSettingsFormData();
  const updateGeneralSettings = useUpdateGeneralSettings();

  const homeDefaultValues: CmsHomepageForm = useMemo(() => {
    const localeDict = constructLocaleDict((lang) => {
      const home = homePageData?.[lang];
      return {
        published: home?.published ?? false,
        websiteTitle: home?.content?.websiteTitle ?? "",
        websiteSubtitle: home?.content?.websiteSubtitle,
        catalogueRaisonneCTA: home?.content?.catalogueRaisonneCTA,
        artistName: home?.content?.artistName,
        birthDate: home?.content?.birthDate,
        deathDate: home?.content?.deathDate,
        resume: home?.content?.resume,
        footerArchives: home?.content?.footerArchives,
      };
    });
    return localeDict;
  }, [homePageData]);

  const generalDefaultValues: CmsGeneralSettingsForm = useMemo(() => {
    return {
      headerAlwaysVisible: generalSettingsData?.headerAlwaysVisible ?? false,
      headerAlwaysVisibleMobile:
        generalSettingsData?.headerAlwaysVisibleMobile ?? false,
      socials: generalSettingsData?.socials ?? [],
    };
  }, [generalSettingsData]);

  const homeForm = useForm<CmsHomepageForm>({
    resolver: zodResolver(homePageFormSchema),
    values: homeDefaultValues,
  });

  const generalForm = useForm<CmsGeneralSettingsForm>({
    resolver: zodResolver(generalSettingsSchema),
    values: generalDefaultValues,
  });

  const { reset: resetHome } = homeForm;
  const { reset: resetGeneral } = generalForm;

  const onSubmitHome = (submittedData: CmsHomepageForm) => {
    if (instanceData?.CMS?.id == null) {
      console.error("Tried to submit but there is no CMS");
      return;
    }

    Object.keys(homeForm.formState.dirtyFields ?? {}).forEach((key) => {
      const localeData = submittedData[key as SupportedLocale];
      const baseData = homePageData?.[key as SupportedLocale];
      if (!localeData) return;

      const mutation = baseData?.content.id ? update : create;
      mutation.mutate(
        {
          id: baseData?.content.id ?? "",
          lang: key as SupportedLocale,
          data: {
            pageName: localeData.websiteTitle,
            published: localeData.published,
            content: {
              websiteTitle: localeData.websiteTitle,
              websiteSubtitle: localeData.websiteSubtitle,
              catalogueRaisonneCTA: localeData.catalogueRaisonneCTA,
              artistName: localeData.artistName,
              birthDate: localeData.birthDate,
              deathDate: localeData.deathDate,
              footerArchives: localeData.footerArchives,
              resume: localeData.resume,
            },
          },
        },
        {
          onSuccess: () => {
            toast({
              title: t("success"),
              description: t("homepageSuccessMessage"),
              duration: 1000,
              variant: "success",
            });
          },
          onError: () => {
            toast({
              title: t("error"),
              description: t("homepageErrorMessage"),
              variant: "error",
              duration: 1000,
            });
          },
        },
      );
    });
  };

  const onSubmitGeneral = (submittedData: CmsGeneralSettingsForm) => {
    if (instanceData?.CMS?.id == null) {
      console.error("Tried to submit but there is no CMS");
      return;
    }

    updateGeneralSettings.mutate(
      {
        id: instanceData?.CMS?.id,
        headerAlwaysVisible: submittedData.headerAlwaysVisible,
        headerAlwaysVisibleMobile: submittedData.headerAlwaysVisibleMobile,
        socials: submittedData.socials,
      },
      {
        onSuccess: () => {
          toast({
            title: t("success"),
            description: t("homepageSuccessMessage"),
            duration: 1000,
            variant: "success",
          });
        },
        onError: () => {
          toast({
            title: t("error"),
            description: t("homepageErrorMessage"),
            variant: "error",
            duration: 1000,
          });
        },
      },
    );
  };

  useEffect(() => {
    resetHome(homeDefaultValues);
  }, [resetHome, homeDefaultValues]);

  useEffect(() => {
    resetGeneral(generalDefaultValues);
  }, [resetGeneral, generalDefaultValues]);

  return (
    <CMSLayout
      pageName={t("cmsTabAccueil")}
      formIds={["homepage", "general-settings"]}
      className="flex flex-col gap-4"
    >
      <FormProvider {...homeForm}>
        <form
          onSubmit={handleSubmitMultilingualForm(
            homeForm,
            homePageData,
            onSubmitHome,
          )}
          id="homepage"
        >
          <div className="flex w-full gap-4">
            <DoubleMultilingualField
              className="w-full"
              corner={(lang) => (
                <Switch dispLabel={false} name={`${lang}.published`} />
              )}
              render={(lang) => <HomepageForm language={lang} />}
            />
          </div>
        </form>
      </FormProvider>

      <FormProvider {...generalForm}>
        <form
          onSubmit={generalForm.handleSubmit(onSubmitGeneral)}
          id="general-settings"
          className="w-full"
        >
          <GeneralSettingsForm />
        </form>
      </FormProvider>
    </CMSLayout>
  );
};

export default CMS;
